import { createAsyncThunk, createSelector } from '@reduxjs/toolkit';
import {
  includesEnumValue,
  InstrumentCompositionEnum,
  PricingMode,
  ProductTypeEnum,
  toBigWithDefault,
  UnifiedLiquidityEnum,
} from '@talos/kyoko';
import type { AppExtraArgument, AppState } from '../../../../providers/AppStateProvider/types';
import { getMarginCostURLWithQueryParams, type MarginCostRequestParams, orderSliceApi } from '../OrderSliceApi';

/** Determines if the current state of the order form is supportive of MarginCost */
export const selectSupportMarginCost = createSelector(
  (state: AppState) => state.order.form.symbolField.value,
  (state: AppState) => state.order.form.orderMarketsField.value,
  (state: AppState) => state.order.referenceData.marketAccounts.marketAccountsByName,
  (state: AppState) => state.order.referenceData.markets.marketsByName,
  (state: AppState) => state.order.form.parameters?.UnifiedLiquidity,
  (state: AppState) => state.order.form.priceModeField.value,
  (state: AppState) => state.order.form.priceField,
  (state: AppState) => state.order.form.quantityField,
  (
    security,
    marketSelectorValue,
    marketAccountsByName,
    marketsByName,
    unifiedLiquidity,
    priceMode,
    priceField,
    quantityField
  ) => {
    if (!security || priceMode !== PricingMode.Default || !priceField.isVisible || !priceField.hasValue) {
      return false;
    }
    const isSecuritySupported =
      includesEnumValue(
        [ProductTypeEnum.Option, ProductTypeEnum.PerpetualSwap, ProductTypeEnum.Future],
        security.ProductType
      ) && security.Composition !== InstrumentCompositionEnum.Synthetic;

    const firstMarketSelection = marketSelectorValue?.selections.at(0);
    const selectedMarketName = firstMarketSelection ? marketAccountsByName.get(firstMarketSelection)?.Market || '' : '';
    const isMarketSupported = marketsByName.get(selectedMarketName)?.Flags?.SupportsMarginCost ?? false;
    const isUnifiedLiquidityEnabled = unifiedLiquidity?.value === UnifiedLiquidityEnum.Enabled;

    // If user type 0, 0.0 don't perform margin cost check
    const zeroPrice = toBigWithDefault(priceField.value, 0).eq(0);
    const zeroQty = toBigWithDefault(quantityField.value, 0).eq(0);

    return isSecuritySupported && isMarketSupported && !isUnifiedLiquidityEnabled && !zeroPrice && !zeroQty;
  }
);

/**
 * Returns the current parameters for making a margin cost request in the current state of the order form
 * If the current state is in any way not supportive of margin cost, this selector will return undefined
 */
export const selectMarginCostRequestParams = createSelector(
  (state: AppState) => state.order.form.symbolField,
  (state: AppState) => state.order.form.quantityField,
  (state: AppState) => state.order.form.sideField,
  (state: AppState) => state.order.form.priceField,
  (state: AppState) => state.order.form.orderCurrencyField,
  (state: AppState) => state.order.form.orderMarketsField,
  selectSupportMarginCost,
  (symbolField, quantityField, sideField, priceField, orderCurrencyField, orderMarketsField, supportsMarginCost) => {
    if (
      !symbolField.value ||
      !sideField.value ||
      !priceField.value ||
      !quantityField.value ||
      !orderMarketsField.initialized
    ) {
      return undefined;
    }

    if (!supportsMarginCost) {
      return undefined;
    }

    return {
      security: symbolField.value,
      quantity: quantityField.value,
      side: sideField.value,
      marketAccounts: orderMarketsField.selections,
      price: priceField.value,
      currency: orderCurrencyField.value,
    } satisfies MarginCostRequestParams;
  }
);

/** Selects the currently getMarginCost response with the current request params state */
export const selectMarginCostResponse = createSelector(
  selectMarginCostRequestParams,
  (state: AppState) => state,
  (requestParams, state) => {
    if (!requestParams) {
      return undefined;
    }

    return orderSliceApi.endpoints.getMarginCost.select(requestParams)(state);
  }
);

/**
 * Selects the MarginCost for the first market account selected in the order form.
 * Note that margin cost is relevant for symbols like perps that are only traded on a single market.
 */
export const selectMarginCost = createSelector(
  (state: AppState) => state.order.form.orderMarketsField.value,
  selectMarginCostResponse,
  (marketsValue, response) => {
    const marketAccount = marketsValue?.selections.at(0);
    if (!response || !response.data?.length || !marketAccount) {
      return undefined;
    }

    const marginForAccount = response.data.find(m => m.MarketAccount === marketAccount);

    return marginForAccount;
  }
);

/**
 * A call to just invalidate and re-get the margin cost given the current state of the order form. If the current
 * state is not supportive of making a request for margin cost, the function will just return early and not do anything.
 */
export const refreshMarginCost = createAsyncThunk<void, undefined, { state: AppState; extra: AppExtraArgument }>(
  'orderSliceApi/refreshMarginCostThunk',
  (_, thunk) => {
    const requestParams = selectMarginCostRequestParams(thunk.getState());
    if (!requestParams) {
      return;
    }

    // Before refreshing the request, we need to invalidate the current request (if any). Otherwise a new request would not be made.
    thunk.dispatch(
      orderSliceApi.util.invalidateTags([{ type: 'MarginCost', id: getMarginCostURLWithQueryParams(requestParams) }])
    );
    thunk.dispatch(orderSliceApi.endpoints.getMarginCost.initiate(requestParams));
  }
);
